import React, { useState, useCallback, useEffect } from "react";
import { TabsWrap } from "./Tabs.style";
import TabList from "./TabList";
import TabPanel from "./TabPanel";

/**
 * Tabs – unified tab component. Tab bar only or with content panels.
 * Use variant + tabsOnly to control appearance.
 *
 * @typedef {Object} TabItem
 * @property {string} key
 * @property {React.ReactNode} label
 * @property {React.ReactNode} [content]
 * @property {boolean} [disabled]
 *
 * @typedef {Object} TabsProps
 * @property {TabItem[]} [tabs] – tab items (key, label, content?, disabled?)
 * @property {{ value: any, label: string }[]} [options] – shorthand for tabs-only (value→key, label)
 * @property {string} [activeKey] – controlled selected key
 * @property {any} [value] – when using options, alias for activeKey
 * @property {string} [defaultActiveKey] – uncontrolled initial key
 * @property {(key: string) => void} [onChange]
 * @property {'horizontal'|'vertical'} [orientation]
 * @property {'default'|'underline'|'pills'|'segments'|'card'} [variant] – segments = pill group (Today/Week/Month style)
 * @property {boolean} [tabsOnly] – render tab bar only, no panels (for options or when content not needed)
 * @property {boolean} [lazy] – mount only active tab content
 * @property {boolean} [keepAlive] – keep mounted tab content when switching
 * @property {boolean} [closable]
 * @property {(key: string) => void} [onClose]
 * @property {() => void} [onAdd]
 * @property {boolean} [fullWidth]
 * @property {string} [className]
 * @property {string} [tabListClassName]
 * @property {string} [tabClassName]
 * @property {string} [activeTabClassName]
 * @property {string} [contentClassName]
 */
export default function Tabs(props) {
  const {
    tabs: tabsProp = [],
    options,
    activeKey: controlledKey,
    value: valueProp,
    defaultActiveKey,
    onChange,
    orientation = "horizontal",
    variant = "default",
    tabsOnly = false,
    lazy = false,
    keepAlive = false,
    closable = false,
    onClose,
    onAdd,
    fullWidth = false,
    className,
    tabListClassName,
    tabClassName,
    activeTabClassName,
    contentClassName,
  } = props;

  const tabs = options
    ? options.map((o) => ({
        key: String(o.value),
        label: o.label,
        content: null,
        originalValue: o.value,
      }))
    : tabsProp;
  const controlledKeyResolved =
    valueProp !== undefined ? String(valueProp) : controlledKey;

  const [internalKey, setInternalKey] = useState(() => {
    const first = tabs.find((t) => !t.disabled);
    return defaultActiveKey ?? first?.key ?? (tabs[0]?.key ?? "");
  });

  const isControlled = controlledKeyResolved !== undefined && controlledKeyResolved !== null;
  const activeKey = isControlled ? controlledKeyResolved : internalKey;

  const [visitedKeys, setVisitedKeys] = useState(() => new Set([activeKey]));

  const handleChange = useCallback(
    (key) => {
      if (!isControlled) setInternalKey(key);
      if (keepAlive) setVisitedKeys((prev) => new Set([...prev, key]));
      const tab = tabs.find((t) => t.key === key);
      const valueToPass = tab?.originalValue !== undefined ? tab.originalValue : key;
      onChange?.(valueToPass);
    },
    [isControlled, keepAlive, onChange, tabs]
  );

  useEffect(() => {
    if (!isControlled && tabs.length > 0 && !tabs.some((t) => t.key === activeKey)) {
      const first = tabs.find((t) => !t.disabled) ?? tabs[0];
      setInternalKey(first.key);
      setVisitedKeys((prev) => new Set([...prev, first.key]));
    }
  }, [isControlled, tabs, activeKey]);

  const shouldRenderContent = useCallback(
    (key) => {
      if (!lazy) return true;
      if (keepAlive) return visitedKeys.has(key);
      return key === activeKey;
    },
    [lazy, keepAlive, visitedKeys, activeKey]
  );

  return (
    <TabsWrap
      className={className}
      $orientation={orientation}
      $tabsOnly={tabsOnly}
    >
      <div className={tabListClassName}>
        <TabList
          tabs={tabs}
          activeKey={activeKey}
          orientation={orientation}
          variant={variant}
          fullWidth={fullWidth}
          closable={closable}
          onAdd={onAdd}
          onSelect={handleChange}
          onClose={onClose}
          tabClassName={tabClassName}
          activeTabClassName={activeTabClassName}
        />
      </div>
      {!tabsOnly &&
        tabs.map((tab) => {
          const render = shouldRenderContent(tab.key);
          const isActive = activeKey === tab.key;
          return (
            <TabPanel
              key={tab.key}
              tabKey={tab.key}
              tabId={`tabpanel-${tab.key}`}
              labelledBy={`tab-${tab.key}`}
              active={isActive}
              contentClassName={contentClassName}
              hidden={!isActive}
            >
              {render ? tab.content : null}
            </TabPanel>
          );
        })}
    </TabsWrap>
  );
}
